/**
 * SETTLE-W1 · 平台周结 API · 真契约源 apps/api-server/src/api_server/routes/platform_settlement.py
 *
 * 周结 = GYG / Viator 等平台给我们打款的周期，不是同行（B2B）结算。
 */
import { api } from "./api";
import type { ReasonPayload } from "./reasonCodes";

export type SettlementCycle = "weekly" | "monthly";
export type SettlementBasis = "travel_date" | "order_date";
export type SettlementStatus = "open" | "pending" | "overdue" | "diff" | "paid";

export interface PeriodPreview { period_start: string; period_end: string; due_date: string }
export interface SettlementPreview { current: PeriodPreview; next: PeriodPreview; basis: SettlementBasis }

export interface PlatformSettlementSetting {
  platform: { code: string; label: string };
  configured: boolean;
  cycle: SettlementCycle | null;
  week_end_weekday: number | null;
  month_end_day: number | null;
  basis: SettlementBasis | null;
  due_days: number | null;
  effective_from: string | null;
  currency: string;
  updated_at: string | null;
  preview: SettlementPreview | null;
}

/** basis 没选时不传（后端 422 并提示「不设默认值」）；前端红字拦在提交前 */
export interface SettlementDraft {
  cycle: SettlementCycle;
  week_end_weekday?: number | null;
  month_end_day?: number | null;
  basis?: SettlementBasis | null;
  due_days: number;
}

export interface SettlementOverviewItem {
  code: string;
  label: string;
  configured: boolean;
  cycle: SettlementCycle | null;
  basis: SettlementBasis | null;
  /** configured 已设置 / pending 待设置（GYG、Viator 这类要设的）/ unset 未设置（不出账期） */
  status: "configured" | "pending" | "unset";
}

export interface SettlementRow {
  id: string;
  platform_code: string;
  platform_label: string;
  period_start: string;
  period_end: string;
  basis: SettlementBasis;
  order_count: number;
  net_cents: number;
  currency: string;
  due_date: string;
  status: SettlementStatus;
  status_label: string;
  overdue_days: number;
  paid_cents: number | null;
  paid_at: string | null;
  reference: string | null;
  diff_cents: number;
  diff_reason: string | null;
  can_record_payment: boolean;
  can_resolve_diff: boolean;
}

export interface SettlementSummary {
  pending_cents: number;
  overdue_cents: number;
  paid_this_month_cents: number;
  diff_cents: number;
}

export interface SettlementLine {
  kind: "order" | "adjustment";
  order_no: string;
  basis_date: string | null;
  total_cents: number | null;
  commission_cents: number | null;
  amount_cents: number;
  note: string | null;
}

const IBASE = "/api/v1/integrations";
const FBASE = "/api/v1/finance/platform-settlements";

export const platformSettlementApi = {
  getSetting: (code: string) => api<PlatformSettlementSetting>(`${IBASE}/platforms/${encodeURIComponent(code)}/settlement`),
  saveSetting: (code: string, draft: SettlementDraft, reason?: ReasonPayload) =>
    api<PlatformSettlementSetting>(`${IBASE}/platforms/${encodeURIComponent(code)}/settlement`, { method: "PUT", body: JSON.stringify({ ...draft, ...(reason ?? {}) }) }),
  preview: (code: string, draft: SettlementDraft) =>
    api<SettlementPreview>(`${IBASE}/platforms/${encodeURIComponent(code)}/settlement:preview`, { method: "POST", body: JSON.stringify(draft) }),
  overview: () => api<{ items: SettlementOverviewItem[] }>(`${IBASE}/settlement-settings`),

  list: (params: { platform?: string; status?: SettlementStatus } = {}) => {
    const q = new URLSearchParams();
    if (params.platform) q.set("platform", params.platform);
    if (params.status) q.set("status", params.status);
    const qs = q.toString();
    return api<{ summary: SettlementSummary; items: SettlementRow[] }>(`${FBASE}${qs ? `?${qs}` : ""}`);
  },
  orders: (id: string) => api<{ settlement: SettlementRow; items: SettlementLine[] }>(`${FBASE}/${id}/orders`),
  recordPayment: (id: string, body: { amount_cents: number; paid_at: string; reference?: string | null }) =>
    api<SettlementRow>(`${FBASE}/${id}:record-payment`, { method: "POST", body: JSON.stringify(body) }),
  resolveDiff: (id: string, reason: ReasonPayload) =>
    api<SettlementRow>(`${FBASE}/${id}:resolve-diff`, { method: "POST", body: JSON.stringify(reason) }),
};

export const WEEKDAY_LABEL = ["日", "一", "二", "三", "四", "五", "六"];
export const BASIS_LABEL: Record<SettlementBasis, string> = { travel_date: "按出行日期", order_date: "按下单日期" };

/** 期间显示：10-26 — 11-01（美西日期，去掉年） */
export function periodLabel(start: string, end: string): string {
  return `${start.slice(5)} — ${end.slice(5)}`;
}
